import { D, roundTo, type DecimalInput } from '../decimal.js';

/**
 * Conversion d'un montant en toutes lettres (français).
 *
 * Orthographe traditionnelle par défaut :
 *  - trait d'union entre dizaines et unités < 100, sauf avec « et » (vingt et un, soixante et onze) ;
 *  - « quatre-vingts » et « cents » ne prennent le « s » que multipliés ET en fin de nombre
 *    (deux cents, quatre-vingts) ou devant million/milliard (deux cents millions) — jamais devant « mille » ;
 *  - « mille » invariable et jamais précédé de « un » ; « million », « milliard » sont des noms (s au pluriel) ;
 *  - « de » avant la devise après million(s)/milliard(s) exacts (un million de francs CFA).
 * Option `reform1990` : traits d'union entre tous les éléments numéraux.
 */

const UNITS = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
  'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize'];
const TENS: Record<number, string> = { 2: 'vingt', 3: 'trente', 4: 'quarante', 5: 'cinquante', 6: 'soixante' };

export interface WordsOptions {
  reform1990?: boolean;
}

/** 0 ≤ n < 100. `final` : le nombre se termine ici (accord de quatre-vingts). */
function below100(n: number, final: boolean, o: WordsOptions): string {
  if (n <= 16) return UNITS[n]!;
  if (n < 20) return 'dix-' + UNITS[n - 10];
  const t = Math.floor(n / 10);
  const r = n % 10;
  if (t === 7 || t === 9) {
    const base = t === 7 ? 'soixante' : 'quatre-vingt';
    const rest = 10 + r;
    if (t === 7 && r === 1) return o.reform1990 ? 'soixante-et-onze' : 'soixante et onze';
    return `${base}-${below100(rest, false, o)}`;
  }
  if (t === 8) {
    if (r === 0) return final ? 'quatre-vingts' : 'quatre-vingt';
    return 'quatre-vingt-' + UNITS[r];
  }
  const tens = TENS[t]!;
  if (r === 0) return tens;
  if (r === 1) return o.reform1990 ? `${tens}-et-un` : `${tens} et un`;
  return `${tens}-${UNITS[r]}`;
}

/** 0 < n < 1000. `final` : rien ne suit, ou suivi d'un nom (million, milliard). */
function below1000(n: number, final: boolean, o: WordsOptions): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const sep = o.reform1990 ? '-' : ' ';
  let out = '';
  if (h > 0) {
    out = h === 1 ? 'cent' : `${UNITS[h]}${sep}cent`;
    if (h > 1 && r === 0 && final) out += 's';
  }
  if (r > 0) out += (out ? sep : '') + below100(r, final, o);
  return out;
}

/** Entier naturel en lettres (jusqu'à 999 999 999 999 999). */
export function integerToWordsFr(value: DecimalInput, options: WordsOptions = {}): string {
  const n = D(value);
  if (!n.isInteger() || n.isNegative()) throw new RangeError('Entier naturel attendu');
  if (n.gte('1e15')) throw new RangeError('Montant trop grand pour la conversion en lettres');
  if (n.isZero()) return 'zéro';
  const sep = options.reform1990 ? '-' : ' ';
  const groups = [
    { size: D('1e12'), singular: 'billion', plural: 'billions', noun: true },
    { size: D('1e9'), singular: 'milliard', plural: 'milliards', noun: true },
    { size: D('1e6'), singular: 'million', plural: 'millions', noun: true },
    { size: D('1e3'), singular: 'mille', plural: 'mille', noun: false },
  ];
  let rest = n;
  const parts: string[] = [];
  for (const g of groups) {
    const q = rest.div(g.size).floor().toNumber();
    rest = rest.mod(g.size);
    if (q === 0) continue;
    if (!g.noun) {
      parts.push(q === 1 ? 'mille' : `${below1000(q, false, options)}${sep}mille`);
    } else {
      parts.push(`${below1000(q, true, options)}${sep}${q === 1 ? g.singular : g.plural}`);
    }
  }
  const last = rest.toNumber();
  if (last > 0) parts.push(below1000(last, true, options));
  return parts.join(sep);
}

export interface CurrencyWords {
  singular: string;
  plural: string;
  /** Sous-unité (centimes) ; null si la devise n'a pas de décimales usuelles. */
  subSingular: string | null;
  subPlural: string | null;
  decimals: number;
}

export const CURRENCY_WORDS: Record<string, CurrencyWords> = {
  XAF: { singular: 'franc CFA', plural: 'francs CFA', subSingular: null, subPlural: null, decimals: 0 },
  XOF: { singular: 'franc CFA', plural: 'francs CFA', subSingular: null, subPlural: null, decimals: 0 },
  EUR: { singular: 'euro', plural: 'euros', subSingular: 'centime', subPlural: 'centimes', decimals: 2 },
  USD: { singular: 'dollar', plural: 'dollars', subSingular: 'cent', subPlural: 'cents', decimals: 2 },
  CDF: { singular: 'franc congolais', plural: 'francs congolais', subSingular: 'centime', subPlural: 'centimes', decimals: 2 },
  GNF: { singular: 'franc guinéen', plural: 'francs guinéens', subSingular: null, subPlural: null, decimals: 0 },
  RWF: { singular: 'franc rwandais', plural: 'francs rwandais', subSingular: null, subPlural: null, decimals: 0 },
  BIF: { singular: 'franc burundais', plural: 'francs burundais', subSingular: null, subPlural: null, decimals: 0 },
  DJF: { singular: 'franc djiboutien', plural: 'francs djiboutiens', subSingular: null, subPlural: null, decimals: 0 },
  KMF: { singular: 'franc comorien', plural: 'francs comoriens', subSingular: null, subPlural: null, decimals: 0 },
  MGA: { singular: 'ariary', plural: 'ariary', subSingular: 'iraimbilanja', subPlural: 'iraimbilanja', decimals: 2 },
  MAD: { singular: 'dirham', plural: 'dirhams', subSingular: 'centime', subPlural: 'centimes', decimals: 2 },
  DZD: { singular: 'dinar', plural: 'dinars', subSingular: 'centime', subPlural: 'centimes', decimals: 2 },
  TND: { singular: 'dinar', plural: 'dinars', subSingular: 'millime', subPlural: 'millimes', decimals: 3 },
  MRU: { singular: 'ouguiya', plural: 'ouguiyas', subSingular: 'khoum', subPlural: 'khoums', decimals: 2 },
  NGN: { singular: 'naira', plural: 'nairas', subSingular: 'kobo', subPlural: 'kobos', decimals: 2 },
  GHS: { singular: 'cedi', plural: 'cedis', subSingular: 'pesewa', subPlural: 'pesewas', decimals: 2 },
  KES: { singular: 'shilling', plural: 'shillings', subSingular: 'cent', subPlural: 'cents', decimals: 2 },
  ZAR: { singular: 'rand', plural: 'rands', subSingular: 'cent', subPlural: 'cents', decimals: 2 },
};

/** Libellé court d'une devise à côté d'un montant. XAF et XOF s'écrivent tous deux FCFA : voir `currencyLongName`. */
export const currencyLabel = (code: string) => (code === 'XAF' || code === 'XOF' ? 'FCFA' : code);

/** Mention complète, pour ne jamais confondre le franc CFA d'Afrique centrale (XAF) et celui d'Afrique de l'Ouest (XOF). */
export function currencyLongName(code: string): string {
  if (code === 'XAF') return 'francs CFA BEAC (XAF)';
  if (code === 'XOF') return 'francs CFA BCEAO (XOF)';
  const c = CURRENCY_WORDS[code];
  return c ? `${c.plural} (${code})` : code;
}

export interface AmountWordsOptions extends WordsOptions {
  /** Majuscule initiale (par défaut : oui). */
  capitalize?: boolean;
}

/**
 * Montant en lettres avec devise.
 * amountToWords(20785707, 'XAF') → « Vingt millions sept cent quatre-vingt-cinq mille sept cent sept francs CFA »
 */
export function amountToWords(amount: DecimalInput, currency = 'XAF', options: AmountWordsOptions = {}): string {
  const c = CURRENCY_WORDS[currency];
  if (!c) throw new RangeError(`Devise non prise en charge pour la conversion en lettres : ${currency}`);
  const value = roundTo(D(amount), c.decimals);
  if (value.isNegative()) throw new RangeError('Montant négatif');
  const whole = value.floor();
  const cents = value.minus(whole).mul(10 ** c.decimals).toNumber();

  let text = integerToWordsFr(whole, options);
  const wholeNum = whole.toNumber();
  const endsWithNoun = /(?:million|milliard|billion)s?$/.test(text);
  text += endsWithNoun ? ` de ${c.plural}` : ` ${wholeNum <= 1 ? c.singular : c.plural}`;

  if (cents > 0 && c.subSingular && c.subPlural) {
    text += ` et ${integerToWordsFr(cents, options)} ${cents === 1 ? c.subSingular : c.subPlural}`;
  }
  return options.capitalize === false ? text : text.charAt(0).toUpperCase() + text.slice(1);
}
